import ErrorMessage from '@/layout/Error/Error';
import MessagePage from '@/pages/message/MessagePage';
import styled from '@emotion/styled';
import { Component, ErrorInfo, PropsWithChildren } from 'react';

import Button from '../Button/Button';

const Fallback = styled.div`
  text-align: center;

  .sup-button {
    margin-top: 1rem;
  }
`;

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Catches render errors thrown anywhere in its subtree and shows the existing
 * generic error surface (the same MessagePage + Error the /error route paints)
 * plus a reload control, instead of letting React unmount the tree into a blank
 * page. React 18 has no hook equivalent for error boundaries, so this stays a
 * class component.
 *
 * WHAT THIS CANNOT CATCH — a boundary only sees throws during render, commit
 * lifecycles and constructors of its own subtree. Items 1, 2 and 4 are now
 * covered by the global error sink (`src/util/errorSink.ts`, installed from
 * `src/index.tsx` — ISSUE-040), which records them on the console; item 3
 * still has no handler:
 *   1. Event handlers (onClick, onChange) — they run outside the render phase.
 *      Async callbacks too: setTimeout/requestAnimationFrame and every
 *      `await` continuation, including the upload/WebSocket job paths. The
 *      sink's `window` `error` listener records these.
 *   2. Unhandled promise rejections — the sink's `window` `unhandledrejection`
 *      listener records these; a boundary never sees them.
 *   3. Errors thrown by this fallback itself, or by anything ABOVE the
 *      outermost boundary — e.g. the `document.getElementById('root')` cast in
 *      `src/index.tsx` — which still blank the page. Nothing catches these:
 *      the cast runs before the boundary exists, and a fallback that throws
 *      has no boundary above it.
 *   4. Errors swallowed downstream. The two silent-discard branches in
 *      `src/components/FileInput/DropZone.tsx` were resolved as a deliberate,
 *      documented ignore (a rejected file is an expected outcome, not an
 *      error); a promise-returning WebSocket listener that rejects now reaches
 *      the sink from the provider's dispatch.
 * Recovery is reload-only by design: `hasError` is never reset, so the subtree
 * cannot retry in place (see the issue's Scope: Out).
 */
class ErrorBoundary extends Component<PropsWithChildren, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // No external error monitoring in this app — the console is the only sink.
    // Kept in production builds: transform-remove-console excludes `error`.
    console.error(error, errorInfo.componentStack);
  }

  /**
   * ISSUE-042: a crash unmounts the subtree, so focus drops to `document.body`
   * and neither a keyboard nor a screen-reader user is told anything happened.
   * Moving focus here IS the announcement mechanism: `role="alert"` is kept as
   * belt-and-braces, but a live region that enters the DOM in the same commit
   * as its content is not reliably announced, whereas focusing the container
   * makes AT read it and puts the keyboard user one Tab from Reload.
   *
   * A ref callback, not an effect — this is a class component, and
   * `componentDidCatch` runs before the fallback exists. It is a stable method
   * rather than an inline arrow so a re-render cannot re-run it and yank focus
   * back off Reload, and it re-checks `activeElement` so StrictMode's
   * detach/attach cycle is a no-op the second time.
   */
  private focusFallback = (node: HTMLDivElement | null) => {
    if (node && document.activeElement !== node) {
      node.focus();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <MessagePage
          message={
            <Fallback ref={this.focusFallback} role="alert" tabIndex={-1}>
              <ErrorMessage />
              {/* `lg`, not the default `md`: at the app's 12.8px rem base the
                  only crash-recovery control in the app renders ~25px tall at
                  `md`, barely over the 24x24 CSS px floor of WCAG 2.2
                  SC 2.5.8. */}
              <Button size="lg" onClick={() => window.location.reload()}>
                Reload
              </Button>
            </Fallback>
          }
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
